import { useMemo } from 'react';
import { Link, useLoaderData, useSearchParams } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import type { MagazineDirectoryTileViewModel } from '~/modules/magazine/type';
import { useTracker } from '@archipro-website/tracker';
import type { loader } from '~/routes/_app.digital-magazines._index';

interface MagazineCardProps {
    magazine: MagazineDirectoryTileViewModel;
}

type MagazineType =
    | 'Dedicated EDM'
    | 'ArchiPros Talk'
    | 'Build'
    | 'The Project Portfolio'
    | 'Curated Collection'
    | 'Commercial Edit'
    | 'Project Feature'
    | 'Misc';

/**
 * A magazine card component for the digital magazines page. Contains a cover
 * image and details about the magazine.
 */
export const MagazineCard = ({ magazine }: MagazineCardProps) => {
    const {
        id,
        link,
        date,
        type,
        imageSrc,
        title,
        subtitle,
        formattedTitleHTML,
        frameStyle,
    } = magazine;
    const { year, month } = useLoaderData<typeof loader>();
    const tracker = useTracker();

    const colorClass = getClassByType(type);
    const showLegacyThumbnails = frameStyle === 'Frameless';

    const [searchParams] = useSearchParams();
    const magazineLink = useMemo(() => {
        const newParams = new URLSearchParams();
        for (const param of ['page', 'sort']) {
            const paramValue = searchParams.get(param);
            if (paramValue) {
                newParams.append(param, paramValue);
            }
        }
        if (year) {
            newParams.append('year', year);
        }
        if (month) {
            newParams.append('month', month);
        }
        return `${link}?${newParams.toString()}`;
    }, [link, searchParams, year, month]);

    return (
        <div>
            <Link
                className="block h-85 md:h-181"
                to={magazineLink}
                preventScrollReset
                prefetch="intent"
                onClick={() => {
                    tracker.log('ArticleDigitalMagazineClick', {
                        url: new URL(window.location.href),
                        targetTracker: 'archiproTracker',
                        data: {
                            // MagazineID
                            ExtraData: id,
                        },
                    });
                }}
            >
                {showLegacyThumbnails ? (
                    <Image className="size-full" src={imageSrc} alt={title} />
                ) : (
                    <div
                        className={cn(
                            'flex size-full flex-col p-2 md:p-4',
                            colorClass,
                        )}
                    >
                        <div
                            className="mb-1.5 line-clamp-2 min-h-9 text-4.5 leading-none md:mb-8 md:min-h-18 md:text-9"
                            dangerouslySetInnerHTML={{ __html: subtitle }}
                        ></div>
                        <div
                            className="mb-2 min-h-13 text-6 leading-[1.1] tracking-[-0.02em] md:mb-4 md:min-h-24 md:text-12"
                            dangerouslySetInnerHTML={{
                                __html: formattedTitleHTML,
                            }}
                        ></div>
                        <Image
                            className="min-h-0 flex-1"
                            src={imageSrc}
                            alt={title}
                            fit="cover"
                        />
                    </div>
                )}
            </Link>
            <h2 className="mt-4 space-y-1 px-4 text-gray-800 md:px-0">
                <Link
                    className="line-clamp-2 text-sm leading-tight hover:underline md:text-lg"
                    to={magazineLink}
                    preventScrollReset
                    prefetch="intent"
                >
                    {title}
                </Link>
                <div className="text-sm leading-tight md:text-lg">{date}</div>
            </h2>
        </div>
    );
};

/**
 * Get the class for the colors for the magazine card based on the type.
 * @param type The type of magazine.
 * @returns The class for the magazine card.
 */
function getClassByType(type: string) {
    switch (type as MagazineType) {
        case 'ArchiPros Talk':
            return 'bg-[#8E8580] text-[#E5E5E5]';
        case 'Commercial Edit':
            return 'bg-[#D4D1CC] text-[#343434]';
        case 'Curated Collection':
            return 'bg-[#000000] text-[#E5E5E5]';
        case 'Project Feature':
        case 'Dedicated EDM':
        case 'Build':
        case 'The Project Portfolio':
        default:
            return 'bg-[#989485] text-[#E5E5E5]';
    }
}
